import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../store';
import { ChatSliceStateSchema, ConversationStateSchema } from 'src/types/chat';

export const rootSelector = (state: RootState): ChatSliceStateSchema =>
    state.chat;

const createEmptyConversationState = (): ConversationStateSchema => ({
    conversationId: 0,
    entityId: '',
    conversationName: '',
    projectId: undefined,
    projectName: '',
    messages: [],
    isLoading: false,
    conversationError: undefined,
    unreadMessageCount: 0,
    replyMessage: undefined,
    lastReadMessageId: 0,
    viewPageNumber: 0,
    searchTerm: '',
    hasNextPage: false,
    hasPreviousPage: false
});

const EMPTY_CONVERSATION_STATE = createEmptyConversationState();

export interface SelectedConversationState extends ConversationStateSchema {
    isOpen: boolean;
    activeConversationId: number | null;
    activeDiscussionsTabId: string | null;
    permissionsLoading: boolean;
    targetMessageId: number | null;
}

export const ChatSelectors = (
    conversationId?: number
): SelectedConversationState => {
    return useSelector((state: RootState) => {
        const chatState = rootSelector(state);
        const targetId = conversationId ?? chatState.activeConversationId ?? 0;
        const conversation = chatState.conversations[targetId] ?? undefined;

        return {
            ...EMPTY_CONVERSATION_STATE,
            ...conversation,
            conversationId: conversation?.conversationId ?? targetId ?? 0,
            messages:
                conversation?.messages ?? EMPTY_CONVERSATION_STATE.messages,
            searchTerm: conversation?.searchTerm ?? '',
            isLoading:
                conversation?.isLoading ?? EMPTY_CONVERSATION_STATE.isLoading,
            isOpen: chatState.isOpen,
            activeConversationId: chatState.activeConversationId,
            activeDiscussionsTabId: chatState.activeDiscussionsTabId,
            permissionsLoading: chatState.permissionsLoading,
            targetMessageId: chatState.targetMessageId ?? null
        };
    }, shallowEqual);
};

export const useConversations = () =>
    useSelector((state: RootState) => rootSelector(state).conversations);

export const useActiveConversationId = () =>
    useSelector((state: RootState) => rootSelector(state).activeConversationId);
